/**
 * Custom event for route change
 */
export interface IRouteChangeEvent<T> extends CustomEvent {
  detail: T;
}

/**
 * Route change event details
 */
export interface IRouteChangeEventDetail {
  /**
   * The time at which route change was started
   */
  startTime: number;
}

/**
 * Telemetry data for page navigation scenario
 */
export interface IScenarioTelemetryData {
  /**
   * Indicates whether the document was hidden when the scenario ended
   */
  backgroundTab: boolean;

  /**
   * Duration of the scenario.
   */
  elapsedTime: number;

  /**
   * Indicates that the scenario is a web scenario that is measuring time since page-navigate-start.
   */
  pageRefreshScenario: boolean;

  /**
   * Indicates the timezone offset from UTC (in minutes) for the user. This can be used to do approximate geo analysis on perf data
   */
  timeZoneOffset: number;
}

/**
 * Map native PerformanceEntry objects for serialization
 */
export interface IMappedPerformanceEntry {
  name: string;
  startTime: number;
  duration: number;
}

export interface INavigationEvent {
  name: string;
  startEvent: string;
  endEvent: string;
  perfEntry?: IMappedPerformanceEntry;
  isAggregate?: boolean;
}

/**
 * Split timing whithin performance scenario
 */
export interface ISplitTiming {
  /**
   * Name of split timing
   */
  name: string;

  /**
   * timestamp of split timing
   */
  timestamp: number;

  /**
   * duration of timings. This is only valid when there is a start/end split timing pair.
   */
  duration?: number;
}

/**
 * Service to monitor performance of page navigation
 */
export interface IPerformanceService {
  /**
   * Service calculates navigation timings from request start to the specified end time and returns that.
   * If endTime was not provided, current time is used to calculate page load time.
   *
   * @param properties - telemetry data to publish
   * @param endTime - Can optionally indicate when a page load ended
   */
  getTTIData(
    properties?: { [name: string]: string },
    endTime?: number
  ): { timingData: IPerformanceTimingData; logProperties: { [name: string]: string | number | boolean } };

  /**
   * Adds a navigation split timing
   *
   * @param name name of split timing
   */
  addSplitTiming(name: string): void;

  /**
   * Starts a performance measurement with the given name
   *
   * @param name name of performance measurement
   */
  startMeasurement(name: string): void;

  /**
   * Ends a performance measurement with the given name. If the name was not found, a split timing will be added w/o duration.
   *
   * @param name name of performance measurement
   */
  endMeasurement(name: string): void;

  /**
   * Executes the specified async function and measures the elapsed time.
   *
   * @param name name of performance measurement block
   * @param fn async function to execute and measure
   */
  measure<R>(name: string, fn: () => Promise<R>): Promise<R>;

  /**
   * Resets page load state
   */
  resetPageLoadState(): void;

  /**
   * Returns elapsed time in minutes since initial page load
   */
  getElapsedTimeInMinutes(): number;
}

/**
 * Event types that fired manually from code.
 */
export enum EventType {
  RouteChange = 'routeChange',
}

export interface IPerformanceTimingData {
  /**
   * navigation start time
   */
  navigationStartTime: number;

  /**
   * elapsed time in milliseconds from navigationStart to connectEnd
   */
  preRequestTime?: number;

  /**
   * elapsed time in milliseconds from fetchStart to domainLookupStart
   */
  fetchTime?: number;

  /**
   * elapsed time in milliseconds from domainLookupStart to domainLookupEnd
   */
  DNSTime?: number;

  /**
   * elapsed time in milliseconds from connectStart to connectEnd
   */
  TCPTime?: number;

  /**
   * elapsed time in milliseconds from connectEnd to responseStart
   */
  requestTime?: number;

  /**
   * elapsed time in milliseconds from responseStart to responseEnd
   */
  responseTime?: number;

  /**
   * elapsed time in milliseconds from request start to response end time
   */
  serverTime?: number;

  /**
   * split timings
   */
  splitTimings?: ISplitTiming[];

  /**
   * elapsed time in milliseconds from request start to page load complete
   */
  pageLoadTime: number;

  isPageLoadComplete?: boolean;
}
